import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface PedigreeNameEditorProps {
  /** Current saved name ('' when unnamed). */
  readonly value: string;
  /** Persist the (trimmed) name. Called only when the value actually changed. */
  readonly onSave: (next: string) => void;
  readonly saving?: boolean;
  /**
   * Whether the edit (pencil) affordance is shown. When false the field is a
   * read-only display — used on the pedigree panel to hide editing in any view
   * mode (values / all) and in read-only. Defaults to true.
   */
  readonly canEdit?: boolean;
  readonly placeholder?: string;
  /** Text shown (muted) when the name is empty and not editing. */
  readonly emptyLabel?: string;
  readonly ariaLabel?: string;
  /** Extra classes for the outer inline wrapper. */
  readonly className?: string;
  /** Extra classes for the read-mode value span (e.g. flex-1 truncate). */
  readonly valueClassName?: string;
}

/**
 * PTV2-1157 — the pencil-wrapped "Pedigree name" field, mirroring the family
 * label editor (CustomLabelEditor): a read display + pencil, which swaps to an
 * input with save/cancel. Used on the patient-record pedigree tile and in the
 * pedigree properties panel (where `canEdit` gates the pencil to edit mode).
 */
export function PedigreeNameEditor({
  value,
  onSave,
  saving = false,
  canEdit = true,
  placeholder,
  emptyLabel,
  ariaLabel,
  className,
  valueClassName,
}: PedigreeNameEditorProps): ReactElement {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Focus the input when entering edit mode (it isn't mounted until then).
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  // Abandon an open edit if the affordance is revoked (e.g. panel leaves edit mode).
  useEffect(() => {
    if (!canEdit && editing) {
      setEditing(false);
      setDraft(value);
    }
  }, [canEdit, editing, value]);

  const label = ariaLabel ?? t('routes.pedigree.legend.pedigreeName', { defaultValue: 'Pedigree name' });

  const commit = (): void => {
    const next = draft.trim();
    if (next !== value.trim()) {
      onSave(next);
    }
    setEditing(false);
  };

  const cancel = (): void => {
    setDraft(value);
    setEditing(false);
  };

  if (editing && canEdit) {
    return (
      <span className={cn('inline-flex min-w-0 items-center gap-1', className)}>
        <Input
          ref={inputRef}
          value={draft}
          disabled={saving}
          aria-label={label}
          placeholder={placeholder}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
          className="h-6 min-w-0 flex-1 px-1.5 text-xs"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          aria-label={t('ui.dialog.close')}
          disabled={saving}
          onClick={cancel}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          size="icon"
          className="h-6 w-6 shrink-0"
          aria-label={t('routes.family.overview.label.save', { defaultValue: 'Save' })}
          disabled={saving}
          onClick={commit}
        >
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </span>
    );
  }

  const isEmpty = value.trim().length === 0;
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1', className)}>
      <span
        className={cn(
          'min-w-0 truncate text-xs',
          isEmpty ? 'italic text-muted-foreground' : 'text-foreground',
          valueClassName,
        )}
        title={isEmpty ? undefined : value}
      >
        {isEmpty ? (emptyLabel ?? placeholder ?? '') : value}
      </span>
      {canEdit && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          aria-label={label}
          onClick={() => {
            setDraft(value);
            setEditing(true);
          }}
        >
          <Pencil className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
        </Button>
      )}
    </span>
  );
}
